import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications, useUnreadCount, useMarkRead, useMarkAllRead, useDeleteNotification } from '@/hooks/useNotifications';
import { formatDistanceToNow, isToday, isYesterday, format } from 'date-fns';
import { WaveLoader } from '@/components/ui/WaveLoader';

// Notification types for filtering and styling
type NotificationType = 'all' | 'unread' | 'assignment' | 'system';

// Notification priority levels for visual hierarchy
type Priority = 'high' | 'medium' | 'low';

interface NotificationItem {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  depositId?: string;
  isRead: boolean;
  createdAt: string;
  deposit?: {
    id: string;
    amount: number;
    description: string;
    depositDate: string;
  };
}

// Get priority based on notification type
const getPriority = (notification: NotificationItem): Priority => {
  if (notification.type === 'assignment' && !notification.isRead) return 'high';
  if (notification.type === 'mention') return 'high';
  if (notification.type === 'status_change') return 'medium';
  return 'low';
};

// Get icon and colors based on notification type
const getNotificationStyle = (type: string, isRead: boolean) => {
  const styles: Record<string, { icon: string; bgColor: string; iconColor: string; borderColor: string }> = {
    assignment: {
      icon: 'fa-user-check',
      bgColor: isRead ? 'bg-blue-50' : 'bg-blue-100',
      iconColor: 'text-blue-600',
      borderColor: 'border-l-blue-500',
    },
    mention: {
      icon: 'fa-at',
      bgColor: isRead ? 'bg-purple-50' : 'bg-purple-100',
      iconColor: 'text-purple-600',
      borderColor: 'border-l-purple-500',
    },
    status_change: {
      icon: 'fa-exchange-alt',
      bgColor: isRead ? 'bg-green-50' : 'bg-green-100',
      iconColor: 'text-green-600',
      borderColor: 'border-l-green-500',
    },
    welcome: {
      icon: 'fa-hand-wave',
      bgColor: isRead ? 'bg-amber-50' : 'bg-amber-100',
      iconColor: 'text-amber-600',
      borderColor: 'border-l-amber-500',
    },
    reminder: {
      icon: 'fa-clock',
      bgColor: isRead ? 'bg-orange-50' : 'bg-orange-100',
      iconColor: 'text-orange-600',
      borderColor: 'border-l-orange-500',
    },
    default: {
      icon: 'fa-bell',
      bgColor: isRead ? 'bg-gray-50' : 'bg-gray-100',
      iconColor: 'text-gray-600',
      borderColor: 'border-l-gray-400',
    },
  };
  return styles[type] || styles.default;
};

// Group notifications by time
const groupByTime = (notifications: NotificationItem[]) => {
  const groups: { label: string; notifications: NotificationItem[] }[] = [];
  const today: NotificationItem[] = [];
  const yesterday: NotificationItem[] = [];
  const earlier: NotificationItem[] = [];

  notifications.forEach(n => {
    const date = new Date(n.createdAt);
    if (isToday(date)) {
      today.push(n);
    } else if (isYesterday(date)) {
      yesterday.push(n);
    } else {
      earlier.push(n);
    }
  });

  if (today.length > 0) groups.push({ label: 'Today', notifications: today });
  if (yesterday.length > 0) groups.push({ label: 'Yesterday', notifications: yesterday });
  if (earlier.length > 0) groups.push({ label: 'Earlier', notifications: earlier });

  return groups;
};

export const NotificationCenter: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<NotificationType>('all');
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: notifications = [], isLoading } = useNotifications();
  const { data: unreadCount = 0 } = useUnreadCount();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();
  const deleteNotification = useDeleteNotification();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Filter notifications based on active tab
  const filteredNotifications = useMemo(() => {
    switch (activeFilter) {
      case 'unread':
        return notifications.filter(n => !n.isRead);
      case 'assignment':
        return notifications.filter(n => n.type === 'assignment');
      case 'system':
        return notifications.filter(n => ['welcome', 'status_change', 'reminder'].includes(n.type));
      default:
        return notifications;
    }
  }, [notifications, activeFilter]);

  // Group filtered notifications by time
  const groupedNotifications = useMemo(() => {
    return groupByTime(filteredNotifications);
  }, [filteredNotifications]);

  // Stats for header
  const stats = useMemo(() => {
    const assignmentCount = notifications.filter(n => n.type === 'assignment' && !n.isRead).length;
    return { assignmentCount };
  }, [notifications]);

  const handleNotificationClick = (notification: NotificationItem, e: React.MouseEvent) => {
    // Mark as read
    if (!notification.isRead) {
      markRead.mutate(notification.id);
    }

    // Don't navigate if clicking dismiss button
    if ((e.target as HTMLElement).closest('[data-dismiss]')) {
      return;
    }

    // Navigate to deposit if notification has one
    if (notification.depositId) {
      // Dispatch custom event so Deposits.tsx can open the modal immediately
      // (navigate alone doesn't reliably trigger useEffect when already on /deposits)
      window.dispatchEvent(new CustomEvent('openDeposit', { detail: { depositId: notification.depositId } }));
      // Also update URL for the case where we're NOT on /deposits yet
      navigate(`/deposits?highlight=${notification.depositId}`);
      setIsOpen(false);
      return;
    }

    // Otherwise toggle expand/collapse for non-deposit notifications
    setExpandedId(expandedId === notification.id ? null : notification.id);
  };

  const handleDismiss = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setDismissingId(id);
    setTimeout(() => {
      deleteNotification.mutate(id);
      setDismissingId(null);
    }, 200);
  };

  const handleMarkAllRead = () => {
    markAllRead.mutate();
  };

  const filterTabs: { key: NotificationType; label: string; icon: string }[] = [
    { key: 'all', label: 'All', icon: 'fa-inbox' },
    { key: 'unread', label: 'Unread', icon: 'fa-circle' },
    { key: 'assignment', label: 'Assigned', icon: 'fa-user-check' },
    { key: 'system', label: 'System', icon: 'fa-cog' },
  ];

  const renderNotification = (notification: NotificationItem) => {
    const style = getNotificationStyle(notification.type, notification.isRead);
    const priority = getPriority(notification);
    const isDismissing = dismissingId === notification.id;
    const isExpanded = expandedId === notification.id;
    const isLongMessage = notification.message.length > 100;

    return (
      <div
        key={notification.id}
        onClick={(e) => handleNotificationClick(notification, e)}
        className={`
          group relative px-3 py-2.5 cursor-pointer transition-all duration-200
          border-l-3 ${style.borderColor}
          ${!notification.isRead ? 'bg-white' : 'bg-gray-50/50'}
          ${isDismissing ? 'opacity-0 -translate-x-full' : 'opacity-100 translate-x-0'}
          ${isExpanded ? 'bg-gray-50' : ''}
          hover:bg-gray-50
        `}
      >
        <div className="flex items-start gap-2.5">
          {/* Priority indicator + Icon */}
          <div className="relative flex-shrink-0">
            <div className={`w-8 h-8 rounded-lg ${style.bgColor} flex items-center justify-center`}>
              <i className={`fas ${style.icon} text-xs ${style.iconColor}`}></i>
            </div>
            {priority === 'high' && !notification.isRead && (
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white"></span>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className={`text-sm leading-tight ${!notification.isRead ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                {notification.title}
              </p>
              <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0">
                {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: false })}
              </span>
            </div>

            {/* Message - expandable */}
            <p className={`text-xs text-gray-600 mt-0.5 leading-relaxed ${isExpanded ? '' : 'line-clamp-2'}`}>
              {notification.message}
            </p>

            {/* Show "tap to expand" hint for long messages */}
            {isLongMessage && !isExpanded && (
              <button className="text-[10px] text-blue-500 hover:text-blue-600 mt-0.5 flex items-center gap-1">
                <i className="fas fa-chevron-down text-[8px]"></i>
                Show more
              </button>
            )}
            {isExpanded && isLongMessage && (
              <button className="text-[10px] text-blue-500 hover:text-blue-600 mt-0.5 flex items-center gap-1">
                <i className="fas fa-chevron-up text-[8px]"></i>
                Show less
              </button>
            )}

            {/* Deposit preview if applicable */}
            {notification.deposit && (
              <div className="mt-1.5 flex items-center gap-2 text-xs">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 font-medium">
                  ${notification.deposit.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
                <span className="text-gray-500 truncate">{notification.deposit.description}</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            {notification.depositId && (
              <button
                data-navigate="true"
                className="p-1 text-blue-400 hover:text-blue-600 rounded"
                title="View deposit"
              >
                <i className="fas fa-external-link-alt text-xs"></i>
              </button>
            )}
            <button
              data-dismiss="true"
              onClick={(e) => handleDismiss(e, notification.id)}
              className="p-1 text-gray-400 hover:text-red-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"
              title="Dismiss"
            >
              <i className="fas fa-times text-xs"></i>
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-1.5 rounded-lg transition-colors ${
          unreadCount > 0
            ? 'text-blue-600 hover:bg-blue-50'
            : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
        }`}
        title="Notifications"
      >
        <i className={`fas fa-bell text-lg`}></i>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-[380px] bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900">Notifications</h3>
                {stats.assignmentCount > 0 && (
                  <span className="px-1.5 py-0.5 text-[10px] font-bold bg-blue-100 text-blue-700 rounded-full">
                    {stats.assignmentCount} new
                  </span>
                )}
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium hover:underline"
                >
                  Mark all read
                </button>
              )}
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-1 mt-3">
              {filterTabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveFilter(tab.key)}
                  className={`
                    flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors
                    ${activeFilter === tab.key
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }
                  `}
                >
                  <i className={`fas ${tab.icon} text-[10px]`}></i>
                  {tab.label}
                  {tab.key === 'unread' && unreadCount > 0 && (
                    <span className={`ml-0.5 px-1 py-0.5 rounded text-[9px] ${
                      activeFilter === 'unread' ? 'bg-white/20' : 'bg-gray-200'
                    }`}>
                      {unreadCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Notification List */}
          <div className="max-h-[400px] overflow-y-auto">
            {isLoading ? (
              <div className="p-8 text-center">
                <WaveLoader size="md" className="mx-auto mb-3" />
                <p className="text-sm text-gray-500">Loading notifications...</p>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <i className={`fas ${
                    activeFilter === 'unread' ? 'fa-check-circle text-green-500' :
                    activeFilter === 'assignment' ? 'fa-user-check text-blue-400' :
                    'fa-bell-slash text-gray-400'
                  } text-2xl`}></i>
                </div>
                <p className="font-medium text-gray-900">
                  {activeFilter === 'unread' ? 'All caught up!' :
                   activeFilter === 'assignment' ? 'No assignment notifications' :
                   'No notifications'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {activeFilter === 'unread' ? 'You\'ve read all your notifications' :
                   activeFilter === 'assignment' ? 'Only assignments made by other users appear here.' :
                   'New notifications will appear here'}
                </p>
              </div>
            ) : (
              <div>
                {groupedNotifications.map(group => (
                  <div key={group.label}>
                    {/* Time group header */}
                    <div className="px-3 py-1.5 bg-gray-50 border-y border-gray-100">
                      <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                        {group.label}
                      </span>
                    </div>
                    {/* Notifications in this group */}
                    <div className="divide-y divide-gray-50">
                      {group.notifications.map(notification => renderNotification(notification))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {filteredNotifications.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-100 bg-gray-50/50">
              <span className="text-[10px] text-gray-500">
                {filteredNotifications.length} notification{filteredNotifications.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
